import type { RJSFSchema } from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import { CURRENCY_MAP } from '../../../../utils/currencies';
import { TypographyVariant } from '../../../../utils/theme';
import { FormValue } from '../../../form-value';
import Icon, { IconName } from '../../../icon/icon';
import { IconScale } from '../../../icon/types';
import { Tooltip } from '../../../tooltip/tooltip';
import Typography from '../../../typography/typography';
import type { SubPrice } from '../../widgets/components';
import { Wrapper } from '../schema-form-values.styles';
import type { SchemaFormValuesCopy } from '../types';
import { getPricingAmount } from '../utils';

interface PricingProps {
  pricingFormula?: string;
  employeeContribution?: number;
  currency: string;
  isEmployee?: boolean;
}

interface PricingValueProps {
  value: {
    showPrice: boolean;
    subPrices?: SubPrice[];
    hasDynamicPrice: boolean;
  };
  properties: RJSFSchema;
  copy: SchemaFormValuesCopy;
}

const Pricing = ({
  pricingFormula,
  employeeContribution,
  currency,
  isEmployee,
}: PricingProps) => {
  const { t } = useTranslation();
  const contribution = employeeContribution ?? 0;
  const percentage = isEmployee ? contribution : 100 - contribution;
  return (
    <div>
      {`${t('words.percentage', { defaultValue: 'Percentage' })}: ${percentage}%`}{' '}
      <br />
      {`${t('words.amount', { defaultValue: 'Amount' })}: ${currency}${getPricingAmount(pricingFormula, employeeContribution, isEmployee)}`}
    </div>
  );
};

interface PricingSchemaProperties {
  subPrices?: {
    items?: {
      properties?: {
        pricingFormula?: {
          title?: string;
          properties?: {
            currency?: {
              default?: string;
            };
          };
        };
        employeeContribution?: {
          title?: string;
          description?: string;
        };
      };
    };
  };
  showPrice?: {
    title?: string;
  };
}

/* eslint-disable react/prop-types */
const PricingValues = ({ value, properties, copy }: PricingValueProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { subPrices, showPrice, hasDynamicPrice } = value;

  const props = properties as unknown as PricingSchemaProperties;
  const pricingFormulaProperties =
    props.subPrices?.items?.properties?.pricingFormula;
  const employeeContributionProperties =
    props.subPrices?.items?.properties?.employeeContribution;

  const currencyDefault =
    pricingFormulaProperties?.properties?.currency?.default;
  const currency = currencyDefault
    ? (CURRENCY_MAP[currencyDefault as keyof typeof CURRENCY_MAP] ?? '')
    : '';

  const titleShowPrice = props.showPrice?.title;
  const titlePricingFormula = pricingFormulaProperties?.title;
  const titleEmployeeContribution = employeeContributionProperties?.title;
  const titleEmployerContribution = employeeContributionProperties?.description;

  if (subPrices?.length === 0 || Number(subPrices?.[0].pricingFormula) === 0) {
    return (
      <Wrapper>
        <FormValue label={titlePricingFormula} value={'-'} />
      </Wrapper>
    );
  }

  if (hasDynamicPrice) {
    return (
      <>
        <FormValue
          label={titlePricingFormula}
          value={
            <StyledTooltip
              tooltipText={t('benefit.steps.pricing.dynamicallyCalculatedInfo')}
              tooltipPlacement="bottom"
              noAnchorRole
            >
              <Typography as="span" variant={TypographyVariant.bodyMd1}>
                {t('benefit.steps.pricing.dynamicallyCalculated')}
              </Typography>
              <StyledIcon
                name={IconName.Info}
                scale={IconScale.XS}
                color={theme.palette.neutral[90]}
              />
            </StyledTooltip>
          }
        />
        <FormValue
          label={titleShowPrice}
          value={showPrice ? copy.yes : copy.no}
        />
      </>
    );
  }

  return (
    <>
      {subPrices?.map(
        ({ employeeContribution, pricingFormula }, index: number) => {
          const priceFormulaStr =
            typeof pricingFormula === 'string' ||
            typeof pricingFormula === 'number'
              ? String(pricingFormula)
              : '';
          const employeeContribNum =
            typeof employeeContribution === 'number'
              ? employeeContribution
              : undefined;

          return (
            <Wrapper key={index}>
              <FormValue
                label={titlePricingFormula}
                value={`${currency}${priceFormulaStr}`}
              />
              <FormValue
                label={titleEmployeeContribution}
                value={
                  <Pricing
                    pricingFormula={priceFormulaStr}
                    employeeContribution={employeeContribNum}
                    currency={currency}
                    isEmployee
                  />
                }
              />
              <FormValue
                label={titleEmployerContribution}
                value={
                  <Pricing
                    pricingFormula={priceFormulaStr}
                    employeeContribution={employeeContribNum}
                    currency={currency}
                  />
                }
              />
            </Wrapper>
          );
        }
      )}
      <FormValue
        label={titleShowPrice}
        value={showPrice ? copy.yes : copy.no}
      />
    </>
  );
};
/* eslint-enable react/prop-types */

export default PricingValues;

const StyledTooltip = styled(Tooltip)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(1)};
  `}
`;

const StyledIcon = styled(Icon)`
  display: flex; // Ensure vertical centering
`;
